import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { toast } from '../../lib/toast';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { ACCION_LABEL, ACCIONES_COMERCIALES, type AccionComercial } from '../../../shared/rotacion';

/**
 * «Acciones / Estrategia comercial» del producto (CEO, 22/10/2026):
 * MANTENER, OFERTA · MEJORAR ROTACIÓN o LIQUIDAR. Va siempre al lado del estado.
 */

const TONO: Record<AccionComercial, 'good' | 'warn' | 'bad'> = {
  MANTENER: 'good',
  OFERTA_MEJORAR_ROTACION: 'warn',
  LIQUIDAR: 'bad',
};

export function AccionComercialBadge({ accion }: { accion: string | null | undefined }) {
  const a = (accion ?? 'MANTENER') as AccionComercial;
  return <Badge tone={TONO[a] ?? 'neutral'} title="Acción / estrategia comercial">{ACCION_LABEL[a] ?? a}</Badge>;
}

/** Selector que guarda al momento (vista rápida y sugerencias). */
export function AccionComercialSelect({ productoId, value, onChanged, className }: {
  productoId: string;
  value: string;
  onChanged?: (accion: AccionComercial) => void;
  className?: string;
}) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (accion: AccionComercial) => api.put(`/productos/${productoId}`, { accion_comercial: accion }),
    onSuccess: (_r, accion) => {
      onChanged?.(accion);
      qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
      qc.invalidateQueries({ queryKey: ['producto', productoId] });
      qc.invalidateQueries({ queryKey: ['sugerencias'] });
      toast.success('Acción comercial cambiada', ACCION_LABEL[accion]);
    },
    onError: (err) => toast.error('No se pudo cambiar la acción comercial', (err as Error).message),
  });
  return (
    <Select
      aria-label="Acción / estrategia comercial"
      options={ACCIONES_COMERCIALES.map((a) => ({ value: a, label: ACCION_LABEL[a] }))}
      value={value}
      emptyLabel={null}
      disabled={m.isPending}
      onChange={(e) => m.mutate(e.target.value as AccionComercial)}
      className={className}
    />
  );
}
